import { useState } from "react";
import {
  Autocomplete as MuiAutocomplete,
  Button as MuiButton,
  Stack as MuiStack,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from "@mui/material";
// import { History as MuiHistoryIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Theme } from "common";
import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import StudyRecruitmentDialog from "./StudyRecruitmentDialog";
import VersionsModal from "./VersionsModal";
import { useStudyRecruitment } from "app/services/StudyRecruitmentService/StudyRecruitmentProvider";

const StudyRecruitmentSubheader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showSelectStudyConfirmation, setShowSelectStudyConfirmation] =
    useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const {
    protocolNumber,
    studyRecruitmentData,
    selectedStudy,
    setSelectedStudy,
    studies,
  } = useStudyRecruitment();

  const { irbNumber, nctId, versions } = studyRecruitmentData;
  const originalStudy = studies?.find(
    (study) => study.protocolNumber === protocolNumber,
  );

  const { dirty, resetForm } = useFormikContext();

  const handleSelectStudyConfirmationClose = (choice) => {
    if (choice) {
      if (dirty) {
        setShowConfirmationDialog(true);
      } else {
        setShowSelectStudyConfirmation(false);
        navigate(`/study-recruitment/${selectedStudy.protocolNumber}`);
      }
    } else {
      setSelectedStudy(originalStudy);
      setShowSelectStudyConfirmation(false);
    }
  };

  const handleConfirmationDialogClose = (choice) => {
    if (choice) {
      resetForm();
    } else {
      setSelectedStudy(originalStudy);
      setShowSelectStudyConfirmation(false);
    }
    setShowConfirmationDialog(false);
  };

  return (
    <>
      <MuiStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <MuiStack direction="row" gap={0.5}>
          <MuiStack>
            <MuiTypography
              fontSize={18}
              fontWeight={600}
              lineHeight="22px"
              height="22px"
            >
              {t("studyRecruitment.labels.protocolNumber", {
                protocolNumber,
              })}
            </MuiTypography>
            <MuiTypography fontSize={12} lineHeight="15px" height="15px">
              {irbNumber &&
                t("studyRecruitment.labels.irbNumber", {
                  irbNumber,
                })}
              {nctId && `(${nctId})`}
            </MuiTypography>
          </MuiStack>

          {studies && studies.length > 0 && (
            <MuiButton
              onClick={() => setShowSelectStudyConfirmation(true)}
              sx={{
                color: Theme.palette.primary.dark,
                fontSize: "12px",
                height: "24px",
                textDecoration: "underline",
                textTransform: "none",
              }}
            >
              {t("buttons.standard.selectStudy")}
            </MuiButton>
          )}
        </MuiStack>

        {/* <MuiStack direction="row" gap={1}>
          <MuiButton
            color="inherit"
            variant="outlined"
            onClick={() => setShowVersionsModal(true)}
          >
            <MuiHistoryIcon sx={{ fontSize: "24px", mr: 1 }} />
            {t("buttons.caps.versions")}
          </MuiButton>
        </MuiStack> */}
      </MuiStack>

      {showSelectStudyConfirmation && (
        <StudyRecruitmentDialog
          open={showSelectStudyConfirmation}
          onClose={handleSelectStudyConfirmationClose}
          title={t("studyRecruitment.labels.selectStudy")}
          okLabel={t("buttons.caps.select")}
          cancelLabel={t("buttons.caps.cancel")}
          disabledOkButton={selectedStudy?.protocolNumber === protocolNumber}
        >
          <MuiAutocomplete
            id="pep-study-recruitment-select-study-autocomplete"
            options={studies}
            value={selectedStudy}
            onChange={(e, val) => setSelectedStudy(val)}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) =>
              option.protocolNumber === value?.protocolNumber
            }
            openOnFocus
            disableClearable
            renderInput={(params) => (
              <MuiTextField {...params} variant="outlined" size="small" />
            )}
            renderOption={({ key, ...rest }, option) => (
              <div key={key} style={{ fontSize: "14px" }} {...rest}>
                {option.name}
              </div>
            )}
            sx={{
              width: "500px",
              "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
                // Move dropdown arrow and clear X up to horizontal center
                height: "auto",
              },
              "& .MuiAutocomplete-popupIndicatorOpen": {
                // Stop MUI from rotating the icon by rotating it back
                rotate: "180deg",
              },
              "& .MuiInputLabel-root, .MuiInputBase-input": {
                fontSize: "14.5px",
              },
            }}
          />
        </StudyRecruitmentDialog>
      )}

      {showVersionsModal && (
        <VersionsModal
          isOpen={showVersionsModal}
          onClose={() => setShowVersionsModal(false)}
          protocolNumber={protocolNumber}
          versions={versions || []}
        />
      )}

      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleConfirmationDialogClose}
        title={t("studyRecruitment.confirmationDialog.cancel.title")}
        message={t("studyRecruitment.confirmationDialog.cancel.content")}
        okLabel={t("buttons.caps.discardChanges")}
        cancelLabel={t("buttons.caps.keepEditing")}
      />
    </>
  );
};

export default StudyRecruitmentSubheader;
